import { useEffect, useMemo, useState } from 'react';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { IEventComment, ReactionKind } from '../../Interfaces/IEventInteraction';
import ReactionBar from './ReactionBar';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';
import { primaryButtonSx } from '../../core/pvtaStyles';

interface EventCommentItemProps {
    comment: IEventComment;
    currentUserId: number | null;
    isWorking: boolean;
    onCreateReply: (parentId: number, content: string) => Promise<void>;
    onUpdateComment: (commentId: number, content: string) => Promise<void>;
    onDeleteComment: (commentId: number) => Promise<void>;
    onToggleReaction: (commentId: number, kind: ReactionKind) => Promise<void>;
}

const formatDateLabel = (value: string): string => {
    const date = new Date(value);
    return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const wasEdited = (createdAt: string, updatedAt?: string | null): boolean => {
    if (!updatedAt) {
        return false;
    }
    const created = new Date(createdAt).getTime();
    const updated = new Date(updatedAt).getTime();
    if (Number.isNaN(created) || Number.isNaN(updated)) {
        return false;
    }
    return updated - created > 1000;
};

const EventCommentItem: React.FC<EventCommentItemProps> = ({
    comment,
    currentUserId,
    isWorking,
    onCreateReply,
    onUpdateComment,
    onDeleteComment,
    onToggleReaction,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [replyOpen, setReplyOpen] = useState<boolean>(false);
    const [replyText, setReplyText] = useState<string>('');
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editText, setEditText] = useState<string>(comment.text);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [avatarLoadError, setAvatarLoadError] = useState<boolean>(false);

    const isOwnComment = useMemo(() => currentUserId === comment.user.id, [comment.user.id, currentUserId]);
    const initials = useMemo(() => comment.user.name.slice(0, 2).toUpperCase(), [comment.user.name]);
    const isMenuOpen = Boolean(menuAnchorEl);
    const avatarUrl = useMemo(() => {
        const value = comment.user.avatar_url;
        if (!value) {
            return null;
        }
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
    }, [comment.user.avatar_url]);
    const { src: authenticatedAvatarSrc } = useAuthenticatedImage(avatarUrl);
    const avatarSrc = avatarLoadError || !authenticatedAvatarSrc ? undefined : authenticatedAvatarSrc;
    const isEdited = useMemo(
        () => wasEdited(comment.created_at, comment.updated_at),
        [comment.created_at, comment.updated_at]
    );

    useEffect(() => {
        setAvatarLoadError(false);
    }, [avatarUrl]);

    const handleReplySubmit = async (): Promise<void> => {
        const trimmed = replyText.trim();
        if (!trimmed) {
            return;
        }

        await onCreateReply(comment.id, trimmed);
        setReplyText('');
        setReplyOpen(false);
    };

    const handleEditSave = async (): Promise<void> => {
        const trimmed = editText.trim();
        if (!trimmed) {
            return;
        }

        await onUpdateComment(comment.id, trimmed);
        setIsEditing(false);
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>): void => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (): void => {
        setMenuAnchorEl(null);
    };

    const handleEditFromMenu = (): void => {
        setIsEditing(true);
        handleCloseMenu();
    };

    const handleDeleteFromMenu = async (): Promise<void> => {
        handleCloseMenu();
        await onDeleteComment(comment.id);
    };

    return (
        <Box sx={{ pl: comment.parent_id ? { xs: 1, sm: 3 } : 0, pt: { xs: 0.5, sm: 1 }, minWidth: 0 }}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ pb: { xs: '8px !important', sm: '12px !important' }, px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 1.5 } }}>
                    <Stack direction="row" spacing={{ xs: 1, sm: 1.25 }} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <Avatar
                            src={avatarSrc}
                            imgProps={{
                                onError: () => setAvatarLoadError(true),
                            }}
                            sx={{ width: { xs: 30, sm: 34 }, height: { xs: 30, sm: 34 }, flexShrink: 0 }}
                        >
                            {!avatarSrc && initials}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 700, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                    >
                                        {comment.user.name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {formatDateLabel(comment.created_at)}
                                    </Typography>
                                    {isEdited && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            bearbeitet
                                        </Typography>
                                    )}
                                </Stack>

                                {isOwnComment && (
                                    <>
                                        <IconButton
                                            size="small"
                                            aria-label="Kommentaraktionen"
                                            onClick={handleOpenMenu}
                                            disabled={isWorking}
                                            sx={{ minHeight: 36, minWidth: 36, flexShrink: 0 }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                        <Menu
                                            anchorEl={menuAnchorEl}
                                            open={isMenuOpen}
                                            onClose={handleCloseMenu}
                                        >
                                            <MenuItem onClick={handleEditFromMenu} disabled={isWorking}>
                                                Bearbeiten
                                            </MenuItem>
                                            <MenuItem onClick={() => void handleDeleteFromMenu()} disabled={isWorking}>
                                                Löschen
                                            </MenuItem>
                                        </Menu>
                                    </>
                                )}
                            </Box>

                            {!isEditing && (
                                <Typography
                                    variant="body2"
                                    sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                                >
                                    {comment.text}
                                </Typography>
                            )}

                            {isEditing && (
                                <Box sx={{ mt: 1 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        value={editText}
                                        onChange={({ target }) => setEditText(target.value)}
                                    />
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ mt: 1 }}
                                    >
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => {
                                                setEditText(comment.text);
                                                setIsEditing(false);
                                            }}
                                            disabled={isWorking}
                                            sx={{ minWidth: 0, px: 1.5 }}
                                        >
                                            Abbrechen
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => void handleEditSave()}
                                            disabled={isWorking}
                                            sx={{ ...primaryButtonSx, minWidth: 0, px: 1.5 }}
                                        >
                                            Speichern
                                        </Button>
                                    </Stack>
                                </Box>
                            )}

                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={0.75}
                                flexWrap="nowrap"
                                sx={{ mt: 0.5, minWidth: 0 }}
                            >
                                <Button
                                    size="small"
                                    startIcon={<ReplyOutlinedIcon fontSize="small" />}
                                    onClick={() => setReplyOpen((current) => !current)}
                                    variant="text"
                                    sx={{
                                        minHeight: isMobile ? 28 : 34,
                                        minWidth: 0,
                                        px: 0.5,
                                        py: 0,
                                        textTransform: 'none',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0,
                                    }}
                                >
                                    Antworten
                                </Button>
                                <Box sx={{ minWidth: 0, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                                    <ReactionBar
                                        counts={comment.counts}
                                        userNamesByReaction={comment.reactionUserNames}
                                        viewerEmoji={comment.viewerEmoji}
                                        disabled={isWorking}
                                        onToggle={(kind) => onToggleReaction(comment.id, kind)}
                                    />
                                </Box>
                            </Stack>

                            {replyOpen && (
                                <Box sx={{ mt: 1.25 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        placeholder="Antwort schreiben"
                                        value={replyText}
                                        onChange={({ target }) => setReplyText(target.value)}
                                    />
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ mt: 1 }}
                                    >
                                        <Button
                                            variant="text"
                                            size="small"
                                            onClick={() => {
                                                setReplyOpen(false);
                                                setReplyText('');
                                            }}
                                            disabled={isWorking}
                                            sx={{ minWidth: 0, px: 1.5 }}
                                        >
                                            Abbrechen
                                        </Button>
                                        <Button
                                            size="small"
                                            disabled={isWorking || !replyText.trim()}
                                            onClick={() => void handleReplySubmit()}
                                            sx={{ ...primaryButtonSx, minWidth: 0, px: 1.5 }}
                                        >
                                            Senden
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            {comment.replies.map((reply: IEventComment) => (
                <EventCommentItem
                    key={reply.id}
                    comment={reply}
                    currentUserId={currentUserId}
                    isWorking={isWorking}
                    onCreateReply={onCreateReply}
                    onUpdateComment={onUpdateComment}
                    onDeleteComment={onDeleteComment}
                    onToggleReaction={onToggleReaction}
                />
            ))}
        </Box>
    );
};

export default EventCommentItem;
